import { INovaEntity } from './base';
import { isCarrierUser, isInternalUser, isWarehouseUser, type IUser } from './user';

export const CARRIER_SUPPORTED_COUNTRIES = ['USA', 'BRA', 'CAN', 'COL'] as const;

export type CarrierSupportedCountryCode = (typeof CARRIER_SUPPORTED_COUNTRIES)[number];

/** Known identifier types; new values may be added without schema changes. */
export const COMPANY_IDENTIFIER_TYPES = [
  'usdot',
  'mc',
  'scac',
  'email_domain',
  'cvor',
  'cnpj',
  'nsc',
  'rntrc',
  'nit'
] as const;

export type CompanyIdentifierType = (typeof COMPANY_IDENTIFIER_TYPES)[number];

export interface CompanyIdentifierCountryRule {
  identifierType: CompanyIdentifierType;
  required: boolean;
}

export const COMPANY_IDENTIFIER_RULES_BY_COUNTRY: Record<
  CarrierSupportedCountryCode,
  readonly CompanyIdentifierCountryRule[]
> = {
  USA: [
    { identifierType: 'usdot', required: true },
    { identifierType: 'mc', required: true },
    { identifierType: 'scac', required: false }
  ],
  CAN: [
    { identifierType: 'nsc', required: true },
    { identifierType: 'cvor', required: false }
  ],
  BRA: [
    { identifierType: 'cnpj', required: false },
    { identifierType: 'rntrc', required: true }
  ],
  COL: [{ identifierType: 'nit', required: true }]
};

export type CompanyIdentifierCountryCode = keyof typeof COMPANY_IDENTIFIER_RULES_BY_COUNTRY;

export const COMPANY_IDENTIFIER_COUNTRIES = Object.keys(
  COMPANY_IDENTIFIER_RULES_BY_COUNTRY
) as CompanyIdentifierCountryCode[];

const EMPTY_COMPANY_IDENTIFIER_COUNTRY_RULES: readonly CompanyIdentifierCountryRule[] = [];

export function isSupportedCountry(
  countryCode: string | null | undefined
): countryCode is CarrierSupportedCountryCode {
  if (!countryCode) {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(COMPANY_IDENTIFIER_RULES_BY_COUNTRY, countryCode);
}

export function getCompanyIdentifierRulesByCountry(
  countryCode: string | null | undefined
): readonly CompanyIdentifierCountryRule[] {
  if (!isSupportedCountry(countryCode)) {
    return EMPTY_COMPANY_IDENTIFIER_COUNTRY_RULES;
  }

  return COMPANY_IDENTIFIER_RULES_BY_COUNTRY[countryCode];
}

export function getCompanyIdentifierTypesByCountry(
  countryCode: string | null | undefined
): CompanyIdentifierType[] {
  return getCompanyIdentifierRulesByCountry(countryCode).map(
    ({ identifierType }) => identifierType
  );
}

export function getRequiredCompanyIdentifierTypesByCountry(
  countryCode: string | null | undefined
): CompanyIdentifierType[] {
  return getCompanyIdentifierRulesByCountry(countryCode)
    .filter(({ required }) => required)
    .map(({ identifierType }) => identifierType);
}

export function isCompanyIdentifierTypeValidForCountry(
  countryCode: string | null | undefined,
  identifierType: CompanyIdentifierType
): boolean {
  return getCompanyIdentifierRulesByCountry(countryCode).some(
    companyIdentifierCountryRule => companyIdentifierCountryRule.identifierType === identifierType
  );
}

export const COMPANY_IDENTIFIER_SOURCES = [
  'warehouse',
  'self_reported',
  'internal_user',
  'highway_lookup'
] as const;

export type CompanyIdentifierSource = (typeof COMPANY_IDENTIFIER_SOURCES)[number];

export const COMPANY_IDENTIFIER_VERIFIED_BY = ['internal_user', 'highway'] as const;

export type CompanyIdentifierVerifiedBy = (typeof COMPANY_IDENTIFIER_VERIFIED_BY)[number];

export interface ICompanyIdentifier extends INovaEntity {
  companyId: string;
  identifierType: CompanyIdentifierType;
  identifierValue: string;
  source?: CompanyIdentifierSource | null;
  verifiedBy?: CompanyIdentifierVerifiedBy | null;
  verifiedAt?: Date | null;
}

export function getSourceByUser(user: IUser): CompanyIdentifierSource {
  if (isInternalUser(user)) {
    return 'internal_user';
  }

  if (isWarehouseUser(user)) {
    return 'warehouse';
  }

  if (isCarrierUser(user)) {
    return 'self_reported';
  }

  return 'self_reported';
}
